"use client"

import React, { useRef, useState } from "react"
import { type GameQuery, type MarketOutcome } from "@azuro-org/toolkit"
import cx from "classnames"

import { Icon } from "components/ui"

import { formatMarketOdds } from "@/Overtime/utils/markets"
import { OddsType } from "@/Overtime/enums/markets"
import OddsValue from "../OddsValue/OddsValue"
import type { TicketPosition } from "@/Overtime/types/markets"
import {
  sportMarketAsTicketPosition,
  getPositionTextV2,
} from "@/Overtime/utils/marketsV2"
import { toast } from "react-toastify"
import { useTicket } from "@/contexts/TicketContext"
import { useChainId } from "wagmi"
import useOddsChange from "src/hooks/useOddsChange"
import { sendGTMEvent } from "@next/third-parties/google"
import { PlainModal } from "components/feedback"
import {
  getEnhancedSgpText,
  hasValidSgpCombinedPositions,
  isSgpMarket,
} from "@/Overtime/utils/sgpHelpers"

type OvertimeOutcomeButtonProps = {
  marketName: string
  outcome: MarketOutcome
  game: NonNullable<GameQuery["game"]>
  isGameOpen: boolean
  size?: 28 | 40
  section?: string
  isZeroOdd: boolean
  isCombiningFuturesNotSupported: (
    ticket: TicketPosition[],
    market: MarketOutcome
  ) => boolean
}

const OvertimeOutcomeButton: React.FC<any> = props => {
  const {
    marketName,
    outcome,
    isGameOpen,
    size = 28,
    section,
    positionText: selectionName,
    playerProps,
    isZeroOdd,
    market,
    fullMarket,
    position,
    isCombiningFuturesNotSupported,
  } = props

  // Get the position text using the same logic as Overtime
  const positionText = getPositionTextV2(market, position, true)

  const isGameRegularlyResolved =
    fullMarket.isResolved && !fullMarket.isCancelled

  const isWinner =
    isGameRegularlyResolved &&
    market.winningPositions &&
    market.winningPositions.includes(position)

  const chainId = useChainId()
  const { ticket, updateTicket, removeFromTicket } = useTicket()
  const isSgpMarketFlag = isSgpMarket(market)

  const isLocked = !isGameOpen || isSgpMarketFlag
  const ticketPosition: TicketPosition = sportMarketAsTicketPosition(
    market,
    outcome.position,
    chainId
  )

  const isPlayerNameEmpty = playerProps && playerProps.playerName === ""

  const isActive = ticket.some(
    position =>
      position.gameId === ticketPosition.gameId &&
      position.typeId === ticketPosition.typeId &&
      position.position === ticketPosition.position &&
      position.line === ticketPosition.line &&
      (!isPlayerNameEmpty
        ? position.playerProps.playerId === ticketPosition.playerProps.playerId
        : true)
  )

  const nodeRef = useRef<HTMLDivElement>(null)

  // Add odds change effect for color/icon animation
  useOddsChange({ odds: outcome.odd, nodeRef })

  const buttonClassName = cx(
    "group/button w-full relative flex items-center justify-between ds:px-3 mb:px-2 overflow-hidden",
    "text-caption-13 font-medium border-none rounded-min select-none",
    {
      "hover:text-brand-light-500 hover:bg-brand-900": !isLocked && !isActive,
      "text-black bg-brand-light-200": isActive,
      "bg-accent-grey-800": !isActive,
      "text-accent-grey-200 cursor-not-allowed": isLocked,
      "h-7": size === 28 && !marketName.toLowerCase().includes("sgp"),
      "min-h-10 py-2": size === 40 && !marketName.toLowerCase().includes("sgp"),
      "min-h-16 mb:min-h-14 ds:py-3 mb:py-2":
        marketName.toLowerCase().includes("sgp") && market.combinedPositions,
      "bg-result-button-won":
        !fullMarket.isCancelled &&
        isWinner &&
        fullMarket.isGameFinished &&
        !isZeroOdd,
      "bg-result-button-lost":
        !fullMarket.isCancelled &&
        !isWinner &&
        fullMarket.isGameFinished &&
        !isZeroOdd,
    }
  )

  const titleClassName = cx("text-left truncate", {
    "group-hover/button:text-brand-light-500": !isLocked && !isActive,
    "text-black": isActive,
    "text-accent-grey-200": !isActive || isLocked,
  })
  const oddsClassName = cx("group/odds flex items-center shrink-0 ml-3")
  const arrowClassName = cx(
    "size-4 text-transparent transition-color",
    "group-[.increased]/odds:text-accent-green-700",
    "group-[.decreased]/odds:text-accent-red-700 group-[.decreased]/odds:rotate-180"
  )
  const valueClassName = cx(
    "transition-color",
    "group-[.increased]/odds:text-accent-green-700",
    "group-[.decreased]/odds:text-accent-red-700"
  )

  const [showFuturesModal, setShowFuturesModal] = useState(false)

  const handleClick = () => {
    if (
      isCombiningFuturesNotSupported &&
      isCombiningFuturesNotSupported(ticket, market)
    ) {
      setShowFuturesModal(true)
      return
    }
    if (
      !ticketPosition.live &&
      (ticket.some(position => position.live) || (ticket.length && market.live))
    ) {
      return
    }

    // Check if this position is already in the ticket
    const isAlreadyInTicket = ticket.some(
      position =>
        position.gameId === ticketPosition.gameId &&
        position.typeId === ticketPosition.typeId &&
        position.position === ticketPosition.position &&
        position.line === ticketPosition.line &&
        (!isPlayerNameEmpty
          ? position.playerProps.playerId ===
            ticketPosition.playerProps.playerId
          : true)
    )

    if (isAlreadyInTicket) {
      // Remove from ticket if already present
      removeFromTicket(ticketPosition)
      sendGTMEvent({
        event: "odds_card_clicked",
        selected: false,
        section: section || (market.live ? "Live" : "Upcoming"),
        sports_name: market.sport.name,
        league_name: market.league.name,
        game_name: market.title,
        market_name: marketName,
        game_id: outcome.gameId,
      })
    } else {
      // Add to ticket if not present
      updateTicket({
        ...ticketPosition,
        market: {
          ...market,
          positionNames: market.positionNames || [],
          marketName: positionText || selectionName,
          displayPosition: position ?? market.displayPosition,
          tournamentName: market.tournamentName,
          odd: formatMarketOdds(OddsType.DECIMAL, outcome.odd),
          // Preserve SGP market structure
          ...(market?.type?.startsWith("sgpBuilder") && {
            type: market.type,
            combinedPositions: market.combinedPositions,
            // Ensure the nested array structure is preserved
            ...(market.combinedPositions &&
              market.combinedPositions[0] && {
                combinedPositions: market.combinedPositions,
              }),
          }),
        },
        position: outcome.position,
      })
      sendGTMEvent({
        event: "odds_card_clicked",
        selected: true,
        section: section || (market.live ? "Live" : "Upcoming"),
        sports_name: market.sport.name,
        league_name: market.league.name,
        game_name: market.title,
        market_name: marketName,
        game_id: outcome.gameId,
      })
    }
  }

  return (
    <>
      <button
        className={buttonClassName}
        disabled={isLocked}
        onClick={handleClick}
      >
        <div
          className={cx(
            "min-w-0 flex-1",
            isSgpMarket(market) && hasValidSgpCombinedPositions(market)
              ? "flex flex-col items-start justify-center gap-1 ds:gap-2 mb:gap-1"
              : "flex items-center gap-1"
          )}
        >
          {isLocked && (
            <Icon
              className="shrink-0 size-4 text-accent-grey-400"
              name="interface/lock"
            />
          )}

          {/* SGP Market Display */}
          {isSgpMarket(market) &&
          hasValidSgpCombinedPositions(market) &&
          (
            market.combinedPositions[position] ||
            market.combinedPositions[market?.displayPosition || 0] ||
            market.combinedPositions[0]
          )?.length > 0 ? (
            <div className="flex flex-col gap-1 ds:gap-2 mb:gap-1 min-w-0 flex-1">
              {/* Try to access the correct combination based on position */}
              {(
                market.combinedPositions[position] ||
                market.combinedPositions[market?.displayPosition || 0] ||
                market.combinedPositions[0]
              )?.map((combinedPosition, index) => {
                // Create a proper market object for the individual position
                const individualMarket = {
                  ...market,
                  typeId: combinedPosition.typeId,
                  line: combinedPosition.line,
                  position: combinedPosition.position,
                  homeTeam: market.homeTeam,
                  awayTeam: market.awayTeam,
                  leagueId: market.leagueId,
                }

                let individualPositionText = getPositionTextV2(
                  individualMarket,
                  combinedPosition.position,
                  true
                )

                // Use the enhanced SGP text function that gets everything from Overtime
                const { enhancedText, subText } = getEnhancedSgpText(
                  combinedPosition,
                  individualPositionText || "",
                  market
                )

                return (
                  <div
                    key={index}
                    className="flex items-center gap-1 ds:gap-2 mb:gap-1"
                  >
                    <div className="relative">
                      <div
                        className={cx(
                          "w-2 h-2 ds:w-3 ds:h-3 mb:w-2 mb:h-2 rounded-full border border-accent-grey-300",
                          {
                            "bg-accent-grey-800": !isActive && !isLocked,
                            "bg-brand-light-200": isActive,
                            "group-hover/button:border-brand-light-500":
                              !isActive && !isLocked,
                            "group-hover/button:bg-brand-light-900":
                              !isActive && !isLocked,
                          }
                        )}
                      />
                      {/* Vertical connecting line - only show if not the last item */}
                      {index <
                        (
                          market.combinedPositions[position] ||
                          market.combinedPositions[
                            market?.displayPosition || 0
                          ] ||
                          market.combinedPositions[0]
                        )?.length -
                          1 && (
                        <div
                          className={cx(
                            "absolute left-1/2 top-full w-px h-3 ds:h-4 mb:h-3 bg-accent-grey-300 transform -translate-x-1/2",
                            {
                              "group-hover/button:bg-brand-light-500":
                                !isActive && !isLocked,
                            }
                          )}
                        />
                      )}
                    </div>
                    <span
                      className={cx(
                        "text-caption-11 ds:text-caption-12 mb:text-caption-11",
                        {
                          "text-accent-grey-200": !isActive,
                          "text-black": isActive,
                          "group-hover/button:text-brand-light-500":
                            !isLocked && !isActive,
                        }
                      )}
                    >
                      {enhancedText}
                    </span>
                    {subText && (
                      <span
                        className={cx(
                          "text-caption-11 ds:text-caption-12 mb:text-caption-11",
                          {
                            "text-accent-grey-500": !isActive,
                            "text-accent-grey-200": isActive,
                            "group-hover/button:text-brand-light-500":
                              !isLocked && !isActive,
                          }
                        )}
                      >
                        {subText}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            /* Regular Market Display */
            <>
              {!isPlayerNameEmpty && (
                <div className={titleClassName}>{playerProps.playerName}</div>
              )}
              <div className={titleClassName}>
                {!isPlayerNameEmpty ? `(${selectionName})` : selectionName}
              </div>
            </>
          )}
        </div>
        <div ref={nodeRef} className={oddsClassName}>
          <Icon className={arrowClassName} name="interface/caret_up" />
          <div>
            {isZeroOdd ? (
              fullMarket.isGameFinished ? (
                "Pending"
              ) : (
                "-"
              )
            ) : (
              <OddsValue
                className={valueClassName}
                odds={formatMarketOdds(OddsType.DECIMAL, outcome.odd)}
              />
            )}
          </div>
        </div>
      </button>
      {showFuturesModal && (
        <PlainModal
          closeModal={() => setShowFuturesModal(false)}
          overlayClosable
          withCloseButton
          containerClassName="backdrop-blur bg-accent-grey-980/10"
        >
          <div className="flex flex-col items-center justify-center min-h-[200px] w-full text-center">
            <img
              src="/images/illustrations/smile_sad.png"
              alt="info"
              className="w-16 h-16 mb-4"
            />
            <div className="text-caption-14 font-semibold mb-2">
              Combining futures on parlay is not supported
            </div>
            <button
              className="mt-6 w-full bg-accent-grey-800 text-white rounded-min py-2"
              onClick={() => setShowFuturesModal(false)}
            >
              OK
            </button>
          </div>
        </PlainModal>
      )}
    </>
  )
}

export default OvertimeOutcomeButton
